/**
 * "Editing" an intent: a signed order is immutable, so an edit is a cancel and
 * a new signature, and the UI says so. The Trade screen cancels the original on
 * chain before signing the replacement; if the cancel fails, nothing is signed,
 * since two live orders for one intent can both fill.
 */

/** Router-state key the Orders screen hands the Trade screen. */
export const EDIT_INTENT_STATE_KEY = "editIntent";

export interface EditIntent {
  /** The order being replaced. */
  orderHash: string;
  /** Its `makerTraits`, needed verbatim by `cancelOrder`. */
  makerTraits: string;
  sellToken: string;
  buyToken: string;
  /** Human decimal amount to prefill the SELL box with (may be ""). */
  sellAmount: string;
}

function str(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

/**
 * Router state is user-navigable (a back button, a crafted history entry), so a
 * partial descriptor degrades to "not editing".
 */
export function readEditIntent(state: unknown): EditIntent | null {
  if (state === null || typeof state !== "object") return null;
  const raw = (state as Record<string, unknown>)[EDIT_INTENT_STATE_KEY];
  if (raw === null || typeof raw !== "object") return null;
  const candidate = raw as Record<string, unknown>;
  const orderHash = str(candidate.orderHash);
  const makerTraits = str(candidate.makerTraits);
  const sellToken = str(candidate.sellToken);
  const buyToken = str(candidate.buyToken);
  if (orderHash === null || makerTraits === null || sellToken === null || buyToken === null) {
    return null;
  }
  return {
    orderHash,
    makerTraits,
    sellToken,
    buyToken,
    sellAmount: typeof candidate.sellAmount === "string" ? candidate.sellAmount : "",
  };
}
